import React, { useState } from 'react';
import {
  IconButton, Popover, List, ListItem, ListItemButton,
  ListItemIcon, ListItemText, Tooltip, Snackbar, Divider, Typography, Box,
} from '@mui/material';
import ShareIcon from '@mui/icons-material/Share';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EmailIcon from '@mui/icons-material/Email';
import CheckIcon from '@mui/icons-material/Check';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import { trackEvent, appendUtm } from '../../utils/analytics';

const PLATFORM = 'imc.show';

interface ShareButtonProps {
  url: string;
  title: string;
  description?: string;
  /** Optional — enables per-share analytics */
  bandId?: string;
  bandName?: string;
  /** sx overrides for the trigger IconButton */
  sx?: object;
}

const canNativeShare = typeof navigator !== 'undefined' && !!navigator.share;

const ShareButton: React.FC<ShareButtonProps> = ({ url, title, description, bandId, bandName, sx }) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [copied, setCopied] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);

  const shareText = `Check out ${title} on ${PLATFORM}`;
  const open = Boolean(anchor);
  const analyticsData = { bandId, bandName: bandName || title };

  const handleTriggerClick = async (e: React.MouseEvent<HTMLElement>) => {
    // On mobile browsers that support the Web Share API, use the native sheet.
    // This covers iMessage, AirDrop, WhatsApp, and everything else the user has installed.
    if (canNativeShare) {
      try {
        const trackedUrl = appendUtm(url, 'native', 'share', 'band_share');
        await navigator.share({
          title: shareText,
          text: description ? `${description}\n\n${shareText}` : shareText,
          url: trackedUrl,
        });
        trackEvent('share', { ...analyticsData, channel: 'native_share' });
      } catch {
        // User cancelled or API unsupported — fall through to popover
      }
      return;
    }
    // Desktop: open the manual popover
    setAnchor(e.currentTarget);
  };

  const handleCopy = async () => {
    const trackedUrl = appendUtm(url, 'copy', 'share', 'band_share');
    try {
      await navigator.clipboard.writeText(trackedUrl);
    } catch {
      const el = document.createElement('textarea');
      el.value = trackedUrl;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    }
    trackEvent('share', { ...analyticsData, channel: 'copy' });
    setCopied(true);
    setSnackOpen(true);
    setTimeout(() => setCopied(false), 2000);
    setAnchor(null);
  };

  const handleWhatsApp = () => {
    const trackedUrl = appendUtm(url, 'whatsapp', 'share', 'band_share');
    const text = encodeURIComponent(`${shareText}\n${trackedUrl}`);
    window.open(`https://wa.me/?text=${text}`, '_blank', 'noopener,noreferrer');
    trackEvent('share', { ...analyticsData, channel: 'whatsapp' });
    setAnchor(null);
  };

  const handleLinkedIn = () => {
    const trackedUrl = appendUtm(url, 'linkedin', 'share', 'band_share');
    const params = new URLSearchParams({
      mini: 'true',
      url: trackedUrl,
      title: shareText,
      ...(description ? { summary: description } : {}),
    });
    window.open(`https://www.linkedin.com/shareArticle?${params}`, '_blank', 'noopener,noreferrer,width=600,height=500');
    trackEvent('share', { ...analyticsData, channel: 'linkedin' });
    setAnchor(null);
  };

  const handleEmail = () => {
    const trackedUrl = appendUtm(url, 'email', 'share', 'band_share');
    const subject = encodeURIComponent(shareText);
    const body = encodeURIComponent(
      `${description ? `${description}\n\n` : ''}${shareText}\n${trackedUrl}`
    );
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
    trackEvent('share', { ...analyticsData, channel: 'email' });
    setAnchor(null);
  };

  return (
    <>
      <Tooltip title="Share">
        <IconButton onClick={handleTriggerClick} aria-label="Share" sx={sx}>
          <ShareIcon />
        </IconButton>
      </Tooltip>

      {/* Desktop fallback popover — only shown when Web Share API is unavailable */}
      <Popover
        open={open}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        PaperProps={{
          sx: { borderRadius: 2, minWidth: 220, mt: 1, overflow: 'hidden' },
        }}
      >
        <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
          <Typography variant="caption" color="text.secondary"
            sx={{ fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', fontSize: '0.65rem' }}>
            Share {title}
          </Typography>
        </Box>
        <Divider sx={{ mt: 0.5 }} />
        <List dense disablePadding>
          <ListItem disablePadding>
            <ListItemButton onClick={handleCopy} sx={{ py: 1.25 }}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                {copied
                  ? <CheckIcon fontSize="small" color="success" />
                  : <ContentCopyIcon fontSize="small" />}
              </ListItemIcon>
              <ListItemText primary={copied ? 'Copied!' : 'Copy link'} />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton onClick={handleWhatsApp} sx={{ py: 1.25 }}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <WhatsAppIcon fontSize="small" sx={{ color: '#25D366' }} />
              </ListItemIcon>
              <ListItemText primary="WhatsApp" />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton onClick={handleLinkedIn} sx={{ py: 1.25 }}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <LinkedInIcon fontSize="small" sx={{ color: '#0A66C2' }} />
              </ListItemIcon>
              <ListItemText primary="LinkedIn" />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton onClick={handleEmail} sx={{ py: 1.25 }}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <EmailIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Email" />
            </ListItemButton>
          </ListItem>
        </List>
      </Popover>

      <Snackbar
        open={snackOpen}
        autoHideDuration={2000}
        onClose={() => setSnackOpen(false)}
        message="Link copied to clipboard"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </>
  );
};

export default ShareButton;
